import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {ScaleLinear} from 'd3';
import React from 'react';

import {PolymerLayout} from 'constants/MapsConstants';
import FileStates from 'FileStates';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  AaSequenceNamesQuery,
  AaSequenceNamesQueryVariables,
} from 'molbio/aa-sequences/util/__generated/AaSequenceNamesQuery';
import {ProteinPart} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {
  getAccessibleAaSequenceNamesById,
  AA_SEQUENCE_NAMES_QUERY,
} from 'molbio/aa-sequences/util/AaSequenceQueryUtil';
import {BioentityPopoverContent} from 'molbio/BioentityPopoverContent';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  LINK_TO_AA_SEQUENCE_ARROW_CIRCLE_RADIUS,
  PART_LINK_ARROW_COLOR,
  LINK_NAME_FONT_SIZE,
  PART_ANNOTATION_COLOR,
  HoveredLinkIndices,
  SINGLE_ANNOTATION_RADIUS,
  SINGLE_ANNOTATION_HEIGHT,
  LINK_ANNOTATION_GAP,
} from 'molbio/maps/BiointelligentLinksConstants';
import BiointelligentLinkChip from 'molbio/maps/linear/BiointelligentLinkChip';
import {getLinkToAaSequencePathParams} from 'molbio/maps/linear/BiointelligentLinksRenderParams';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import {getFeatureLayersForRegionModels, LayersDict, span} from 'molbio/maps/linear/LayeringHelpers';
import Type from 'molbio/maps/Type';
import getTestProps from 'test/lib/cypress/getTestProps';
import Popover from 'ui-library/floating-ui/Popover';

// TODO: Can this be factored out? To what extent do we even care about the LayersDict? It seems to always be e.g. {1: 1, 2: 2, etc}
export function getAaSequencePartAnnotationsLayersDict(
  sliceModels: Array<ProteinPart>,
  sequenceLength: number
): LayersDict {
  const {layers} = getFeatureLayersForRegionModels(
    sliceModels,
    sequenceLength,
    (sliceModel) => `${sliceModel.id}`,
    false
  );
  const dedupedLayerIndices = new Set(layers.map(({layer}) => layer));
  const orderedDedupedLayerIndices = [...dedupedLayerIndices].sort((i, j) => i - j);
  return objectFromEntries(orderedDedupedLayerIndices.map((layerIndex, i) => [layerIndex, i]));
}

export interface AaSequencePartAnnotationsProps {
  /** The ordinal y-indexes of the features on this layer. */
  layersDict: LayersDict;
  /** Flat object layout data */
  layout: PolymerLayout;
  /** The length of the protein being rendered */
  aaSequenceLength: number;
  /** A function that triggers a redraw of the slice wrapper. */
  redraw: () => void;
  /** The index at which the slice ends */
  sliceEnd: number;
  /** The Parts that appear on this sequence. */
  sliceParts: Array<ProteinPart>;
  /** The index at which the slice starts */
  sliceStart: number;
  /** A function that takes the index of a residue and returns its x-axis position on the slice */
  xFn: ScaleLinear<number, number>;
  /** A function that takes the y-index of a part annotation and returns its y-axis position on the slice */
  yFn: ScaleLinear<number, number>;
  /** A function to set or expand the selection on the sequence map */
  setSelection: (start: number, end: number, shouldExpand: boolean) => void;
  /** A function called to open the protein context menu */
  openContextMenu: (e: Event) => void;
  setHoveredLinkIndices: (indices: HoveredLinkIndices | null) => void;
}

export default function AaSequencePartAnnotations({
  layersDict,
  layout,
  aaSequenceLength,
  redraw,
  sliceEnd,
  sliceParts,
  sliceStart,
  xFn,
  yFn,
  setSelection,
  openContextMenu,
  setHoveredLinkIndices,
}: AaSequencePartAnnotationsProps): JSX.Element {
  // Always redraw layer wrapper; it'll account for the number of parts
  redraw();

  const result = useBenchlingQuery<AaSequenceNamesQuery, AaSequenceNamesQueryVariables>(
    AA_SEQUENCE_NAMES_QUERY,
    {
      variables: {ids: sliceParts.map(({proteinId}) => proteinId)},
    }
  );
  let accessibleAaSequenceNamesById: Record<string, string> | null = null;
  if (result.type === QueryResultType.SUCCESS) {
    accessibleAaSequenceNamesById = getAccessibleAaSequenceNamesById(result.data);
  }

  const type = new Type(LINK_NAME_FONT_SIZE);
  const {layers} = getFeatureLayersForRegionModels(
    sliceParts,
    aaSequenceLength,
    (sliceModel) => `${sliceModel.id}`,
    false
  );
  const layerIndicesByPartId = layers.reduce<Record<number, number>>((acc, {model, layer}) => {
    acc[model.id] = layer;
    return acc;
  }, {});

  return (
    <svg>
      <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={true}>
        <>
          {sliceParts.map((part) => {
            const partId = part.id;

            const {start, end} = span(sliceStart, sliceEnd, part.start, part.end, aaSequenceLength)!;

            const height = SINGLE_ANNOTATION_HEIGHT;
            const width = Math.max(xFn(end) - xFn(start) - LINK_ANNOTATION_GAP, 0);

            const translateX = xFn(start);
            const translateY = yFn(layersDict[layerIndicesByPartId[partId]]);
            const proteinName = accessibleAaSequenceNamesById?.[part.proteinId] ?? null;

            const {arrowPointD, circleParams, displayNameParams, lineParams} = getLinkToAaSequencePathParams({
              end,
              height,
              proteinName,
              sliceEnd,
              type,
              width,
            });

            const arrowAttrs = {
              stroke: PART_LINK_ARROW_COLOR,
              fill: PART_LINK_ARROW_COLOR,
            };

            return (
              <g
                className="layer-group"
                {...getTestProps({
                  component: 'AaSequencePartAnnotations',
                  element: 'part-annotation',
                  key: partId.toString(),
                })}
                key={partId}
                onMouseEnter={() => {
                  setHoveredLinkIndices({start: part.start, end: part.end, highlightedStrand: 0});
                }}
                onMouseLeave={() => {
                  setHoveredLinkIndices(null);
                }}
                onMouseDown={(e) => {
                  setSelection(part.start, part.end, e.shiftKey);
                }}
                onContextMenu={(e) => {
                  setSelection(part.start, part.end, e.shiftKey);
                  openContextMenu(e.nativeEvent);
                }}
                data-amp-safe={false}
              >
                <Popover
                  renderContent={() => (
                    <BioentityPopoverContent
                      id={part.proteinId}
                      onClickName={() => {
                        FileStates.get().openFolderItem(part.proteinId);
                      }}
                      validationExtras={null}
                    />
                  )}
                >
                  <g
                    className={css(styles.partAnnotation)}
                    {...getTestProps({
                      component: 'AaSequencePartAnnotationsView',
                      element: 'root',
                    })}
                    transform={`translate(${translateX}, ${translateY})`}
                  >
                    <rect
                      fill={PART_ANNOTATION_COLOR}
                      rx={SINGLE_ANNOTATION_RADIUS}
                      ry={SINGLE_ANNOTATION_RADIUS}
                      height={height}
                      width={width}
                    />
                    {/* Arrow to align 'link to protein' dots */}
                    <line
                      x1={lineParams.x1}
                      x2={lineParams.x2}
                      y1={lineParams.y1}
                      y2={lineParams.y2}
                      strokeWidth={2}
                      shapeRendering="crispEdges"
                      {...arrowAttrs}
                    />
                    {/* Point of arrow */}
                    {arrowPointD && (
                      <path d={arrowPointD} shapeRendering="geometricPrecision" {...arrowAttrs} />
                    )}
                    {/* 'Link to protein' dots */}
                    {circleParams.map(({cx, cy}, index) => (
                      <circle
                        key={index}
                        cx={cx}
                        cy={cy}
                        r={LINK_TO_AA_SEQUENCE_ARROW_CIRCLE_RADIUS}
                        {...arrowAttrs}
                      />
                    ))}
                    <BiointelligentLinkChip
                      linkName={displayNameParams.displayName}
                      linkIcon={displayNameParams.iconId}
                      linkType={BiointelligentLinkType.AMINO_ACID_PART}
                      chipMaxWidth={displayNameParams.width}
                      annotationWidth={width}
                    />
                  </g>
                </Popover>
              </g>
            );
          })}
        </>
      </ComponentWrapper>
    </svg>
  );
}

const styles = StyleSheet.create({
  partAnnotation: {
    cursor: 'pointer',
  },
});
